// Submit handler for the QR-upload enquiry form on
// /free-qr-code-guest-photo-uploads/. Wires up Turnstile, validates basics,
// POSTs to the worker's /qr-enquiry endpoint, and toggles inline state.

import { onReady } from "#public/utils/on-ready.js";

const FORM_SELECTOR = "form[data-qr-form]";

const TURNSTILE_WIDGETS = new Map();

const whenTurnstileReady = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    if (window.turnstile) return resolve(window.turnstile);
    const start = Date.now();
    const poll = () => {
      if (window.turnstile) return resolve(window.turnstile);
      if (Date.now() - start > 10000) return resolve(null);
      setTimeout(poll, 100);
    };
    poll();
  });

const renderTurnstile = async (placeholderId, sitekey) => {
  if (!placeholderId || !sitekey) return;
  const placeholder = document.getElementById(placeholderId);
  if (!placeholder) return;
  if (TURNSTILE_WIDGETS.has(placeholderId)) return;
  const turnstile = await whenTurnstileReady();
  if (!turnstile) return;
  try {
    const id = turnstile.render(`#${placeholderId}`, {
      sitekey,
      action: "qr-enquiry",
      theme: "light",
    });
    if (id) TURNSTILE_WIDGETS.set(placeholderId, id);
  } catch (_e) {
    // No-op: render can throw if already mounted.
  }
};

const resetTurnstile = (placeholderId) => {
  if (typeof window === "undefined" || !window.turnstile) return;
  const widgetId = TURNSTILE_WIDGETS.get(placeholderId);
  if (!widgetId) return;
  try {
    window.turnstile.reset(widgetId);
  } catch (_e) {
    // No-op
  }
};

const getValue = (form, name) => {
  const el = form.querySelector(`[name="${name}"]`);
  return el && typeof el.value === "string" ? el.value.trim() : "";
};

const getRadioValue = (form, name) => {
  const checked = form.querySelector(`input[name="${name}"]:checked`);
  return checked ? checked.value : "";
};

const getTurnstileToken = (form) => {
  const el = form.querySelector("[name='cf-turnstile-response']");
  return el && el.value ? el.value : "";
};

const showMessage = (form, kind, text) => {
  const msg = form.querySelector("[data-qr-msg]");
  if (!msg) return;
  msg.hidden = false;
  msg.classList.remove("lb-form__msg--success", "lb-form__msg--error");
  msg.classList.add(`lb-form__msg--${kind}`);
  msg.textContent = text;
};

const clearMessage = (form) => {
  const msg = form.querySelector("[data-qr-msg]");
  if (!msg) return;
  msg.hidden = true;
  msg.textContent = "";
};

const setSubmitting = (form, isSubmitting) => {
  const btn = form.querySelector("[data-qr-submit]");
  if (!btn) return;
  btn.disabled = isSubmitting;
  btn.textContent = isSubmitting ? "Sending…" : "Send request";
};

const buildPayload = (form) => ({
  turnstile_token: getTurnstileToken(form),
  website: getValue(form, "website"),
  first_name: getValue(form, "first_name"),
  last_name: getValue(form, "last_name"),
  email: getValue(form, "email"),
  mobile: getValue(form, "mobile"),
  address: getValue(form, "address"),
  event_date: getValue(form, "event_date"),
  event_type: getValue(form, "event_type"),
  venue: getValue(form, "venue"),
  how_heard: getValue(form, "how_heard"),
  qr_format: getRadioValue(form, "qr_format"),
  other_items: getRadioValue(form, "other_items"),
  message: getValue(form, "message"),
});

const validate = (payload) => {
  if (!payload.first_name || !payload.last_name) {
    return "Please add your first name and surname.";
  }
  if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
    return "Please add a valid email address.";
  }
  if (!payload.mobile) return "Please add a contact number.";
  if (!payload.event_date) return "Please add your event date.";
  if (!payload.event_type) return "Please choose your event type.";
  return null;
};

const submitForm = async (form) => {
  clearMessage(form);

  const workerUrl = (form.dataset.workerUrl || "").trim();
  if (!workerUrl) {
    showMessage(
      form,
      "error",
      "Form isn't configured — please call us on 03331 126993.",
    );
    return;
  }

  const payload = buildPayload(form);
  const validationError = validate(payload);
  if (validationError) {
    showMessage(form, "error", validationError);
    return;
  }

  if (!payload.turnstile_token) {
    showMessage(
      form,
      "error",
      "Please wait for the security check to finish, then submit again.",
    );
    return;
  }

  setSubmitting(form, true);
  let ok = false;
  try {
    const response = await fetch(`${workerUrl.replace(/\/$/, "")}/qr-enquiry`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    ok = response.ok;
  } catch (_err) {
    ok = false;
  }

  if (!ok) {
    const placeholder = form.querySelector("[data-qr-turnstile]");
    if (placeholder && placeholder.id) resetTurnstile(placeholder.id);
    showMessage(
      form,
      "error",
      "Sorry, something went wrong sending your request. Please try again or call us on 03331 126993.",
    );
    setSubmitting(form, false);
    return;
  }

  showMessage(
    form,
    "success",
    "Thanks — we've got it. We'll send your QR code and upload page over shortly.",
  );
  form.reset();
  setSubmitting(form, false);
  const placeholder = form.querySelector("[data-qr-turnstile]");
  if (placeholder && placeholder.id) resetTurnstile(placeholder.id);
};

onReady(() => {
  for (const form of document.querySelectorAll(FORM_SELECTOR)) {
    const placeholder = form.querySelector("[data-qr-turnstile]");
    const sitekey = form.dataset.turnstileKey || "";
    if (placeholder && placeholder.id) {
      renderTurnstile(placeholder.id, sitekey);
    }

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      submitForm(form);
    });
  }
});
